import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { guestLogin } from '@/api/auth'
import { X } from 'lucide-react'

// Mehmon (guest) sifatida kirish vaqtincha o'chirilgan — kodi keyin kerak
// bo'lganda qayta yoqish uchun saqlab qo'yilgan.
const GUEST_LOGIN_ENABLED = false

export default function GuestModal({ onClose, onSuccess }) {
  const { t }     = useTranslation()
  const { login } = useAuth()
  const navigate  = useNavigate()

  const [tab,      setTab]      = useState('choice')
  const [fullName, setFullName] = useState('')
  const [age,      setAge]      = useState('')
  const [gender,   setGender]   = useState('')
  const [error,    setError]    = useState(null)
  const [loading,  setLoading]  = useState(false)

  async function handleGuestSubmit(e) {
    e.preventDefault()
    if (!fullName || !age || !gender) return
    setLoading(true); setError(null)
    try {
      const { data } = await guestLogin(fullName, parseInt(age), gender)
      login({ access: data.access, refresh: data.refresh, user: data.user })
      onSuccess?.()
    } catch (err) {
      const detail = err.response?.data
      setError(typeof detail === 'object' ? Object.values(detail).flat().join(', ') : (detail ?? t('common.error', 'Xatolik')))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 space-y-5"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)' }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg m-0" style={{ color: 'var(--ink)' }}>
            {t('exam.startExam', 'Imtihonni boshlash')}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors"
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink-3)' }}
          >
            <X size={18} />
          </button>
        </div>

        {tab === 'choice' && (
          <div className="space-y-3">
            <p className="text-sm m-0" style={{ color: 'var(--ink-2)' }}>
              {t('exam.authRequired', "Imtihon ishlash uchun kim ekanligingizni ko'rsating:")}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-all"
              style={{ borderColor: 'var(--coral)', color: 'var(--coral)', background: 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--coral-tint)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {t('auth.login', 'Kirish')} (hisob bor)
            </button>
            <button
              onClick={() => navigate('/register')}
              className="w-full py-3 rounded-xl text-sm font-semibold border-2 transition-all"
              style={{ borderColor: 'var(--mint)', color: 'var(--mint)', background: 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--mint-tint)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {t('auth.register', "Ro'yxatdan o'tish")}
            </button>
            {GUEST_LOGIN_ENABLED && (
              <>
                <div className="relative text-center text-sm" style={{ color: 'var(--ink-3)' }}>
                  <span className="px-3" style={{ background: 'var(--surface)', position: 'relative', zIndex: 1 }}>
                    {t('common.or', 'yoki')}
                  </span>
                  <hr className="absolute inset-x-0 top-1/2 -z-0 m-0" style={{ border: 'none', borderTop: '1px solid var(--border)' }} />
                </div>
                <button
                  onClick={() => setTab('guest')}
                  className="w-full py-3 rounded-xl text-sm font-semibold transition-all"
                  style={{ background: 'var(--bg-soft)', border: '1px solid var(--border)', color: 'var(--ink-2)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-soft)'}
                >
                  👤 {t('auth.continueAsGuest', 'Mehmon sifatida davom etish')}
                </button>
              </>
            )}
          </div>
        )}

        {GUEST_LOGIN_ENABLED && tab === 'guest' && (
          <form onSubmit={handleGuestSubmit} className="space-y-4">
            <button
              type="button"
              onClick={() => setTab('choice')}
              className="text-sm font-medium transition-colors"
              style={{ border: 0, background: 'transparent', color: 'var(--coral)', cursor: 'pointer', padding: 0 }}
            >
              ← {t('common.back', 'Orqaga')}
            </button>

            {error && (
              <div className="text-sm rounded-xl px-4 py-3" style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger)', color: 'var(--danger)' }}>
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
                {t('auth.fullName', "To'liq ism")} *
              </label>
              <input
                type="text" value={fullName} onChange={e => setFullName(e.target.value)} required
                className="w-full rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                style={{ border: '1px solid var(--border)', background: 'var(--bg-soft)', color: 'var(--ink)' }}
                placeholder={t('auth.fullNamePlaceholder', 'Familiya Ism')}
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
                  {t('auth.age', 'Yosh')} *
                </label>
                <input
                  type="number" value={age} onChange={e => setAge(e.target.value)} required min={1} max={120}
                  className="w-full rounded-xl px-4 py-2.5 text-sm outline-none"
                  style={{ border: '1px solid var(--border)', background: 'var(--bg-soft)', color: 'var(--ink)' }}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
                  {t('auth.gender', 'Jins')} *
                </label>
                <div className="flex gap-2 h-[42px]">
                  {[
                    { value: 'male',   label: t('auth.male',   'Erkak') },
                    { value: 'female', label: t('auth.female', 'Ayol')  },
                  ].map(({ value, label }) => (
                    <label
                      key={value}
                      className="flex-1 flex items-center justify-center rounded-xl border text-sm font-medium cursor-pointer transition-all select-none"
                      style={
                        gender === value
                          ? { background: 'var(--coral)', borderColor: 'var(--coral)', color: '#fff' }
                          : { background: 'var(--bg-soft)', borderColor: 'var(--border)', color: 'var(--ink-2)' }
                      }
                    >
                      <input
                        type="radio" name="gender" value={value}
                        checked={gender === value} onChange={() => setGender(value)}
                        className="sr-only" required={gender === ''}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all"
              style={{ background: 'var(--coral)', border: 0, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? t('common.loading', 'Yuklanmoqda...') : t('exam.startNow', 'Boshlash')}
            </button>
            <p className="text-xs text-center m-0" style={{ color: 'var(--ink-3)' }}>
              {t('auth.guestNote', "Keyinchalik ro'yxatdan o'tib natijalaringizni saqlashingiz mumkin.")}
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
